import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";

import { addDays, MONTHS, sameDay, startOfWeek, WEEKDAYS } from "./shared";

/** An inclusive span of days, as ISO `YYYY-MM-DD` strings. */
export interface DayRange {
  start: string;
  end: string;
}

/**
 * Inline month grid that picks one day or a span of them.
 *
 * <p>Click a day to choose it. Click a second to close a range, with the span
 * previewing under the cursor as you move. Click again and you start over. That
 * is the whole interaction — no "a day / a range of days" mode to set first, no
 * modifier key to discover, and nothing that needs a mouse: it works the same
 * under a finger.</p>
 *
 * <p>A drag would read more directly, but it can't be adjusted afterwards
 * without starting again, it fights scrolling on touch, and it still needs a
 * click-click fallback for keyboards — so this <em>is</em> the fallback,
 * promoted to the only path.</p>
 */
export function DayRangeCalendar({
  value,
  onChange,
  minDate,
  hidePast,
}: {
  value: DayRange | null;
  onChange: (range: DayRange) => void;
  /** Days before this are not selectable. Defaults to today. */
  minDate?: Date;
  /** Start at the current week and hide earlier weeks/months entirely, rather than
   *  greying out past days (used where only future dates make sense). */
  hidePast?: boolean;
}) {
  const today = React.useMemo(() => startOfDay(new Date()), []);
  const floor = startOfDay(minDate ?? today);

  const [month, setMonth] = React.useState<Date>(() =>
    startOfMonth(value ? fromIso(value.start) : today),
  );
  // The first click of an in-progress range. Null when the range is settled.
  const [anchor, setAnchor] = React.useState<string | null>(null);
  const [hovered, setHovered] = React.useState<string | null>(null);

  // While a range is open, the highlighted span follows the cursor.
  const preview: DayRange | null = anchor
    ? orderRange(anchor, hovered ?? anchor)
    : value;

  function pick(iso: string) {
    if (!anchor) {
      setAnchor(iso);
      onChange({ start: iso, end: iso });
      return;
    }
    setAnchor(null);
    setHovered(null);
    onChange(orderRange(anchor, iso));
  }

  const isCurrentMonth =
    month.getFullYear() === today.getFullYear() && month.getMonth() === today.getMonth();
  let cells = monthCells(month);
  if (hidePast && isCurrentMonth) {
    // Drop whole weeks before the one containing today, so the grid opens on this week.
    const lead = (startOfMonth(month).getDay() + 6) % 7;
    const weekOfToday = Math.floor((lead + today.getDate() - 1) / 7);
    cells = cells.slice(weekOfToday * 7);
  }
  // With hidePast there's nothing to go back to before this month.
  const prevDisabled = hidePast === true && month <= startOfMonth(today);

  return (
    <div className="w-fit rounded-md border border-input bg-background p-2">
      <div className="mb-1 flex items-center justify-between gap-2">
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => setMonth((m) => addMonths(m, -1))}
          disabled={prevDisabled}
          className={cn(
            "rounded border border-input p-0.5 hover:bg-muted",
            prevDisabled && "cursor-not-allowed opacity-40 hover:bg-transparent",
          )}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <span className="font-comic-headline text-[11px] uppercase tracking-wide">
          {MONTHS[month.getMonth()]} {month.getFullYear()}
        </span>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => setMonth((m) => addMonths(m, 1))}
          className="rounded border border-input p-0.5 hover:bg-muted"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-px text-center">
        {WEEKDAYS.map((d) => (
          <div key={d} className="pb-1 text-[9px] uppercase text-muted-foreground">
            {d.slice(0, 2)}
          </div>
        ))}
        {cells.map((day, i) => {
          if (!day) return <div key={`pad-${i}`} />;
          const iso = toIso(day);
          const disabled = day < floor;
          const inRange =
            !!preview && iso >= preview.start && iso <= preview.end;
          const isEdge =
            !!preview && (iso === preview.start || iso === preview.end);
          return (
            <button
              key={iso}
              type="button"
              disabled={disabled}
              aria-pressed={inRange}
              onClick={() => pick(iso)}
              onMouseEnter={() => anchor && setHovered(iso)}
              className={cn(
                "h-7 w-8 text-[11px] transition",
                disabled && "cursor-not-allowed text-muted-foreground/40",
                !disabled && !inRange && "hover:bg-muted",
                inRange && !isEdge && "bg-primary/15",
                isEdge && "bg-primary font-semibold text-primary-foreground",
                sameDay(day, today) && !inRange && "font-bold underline",
              )}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>

      <div className="mt-2 flex flex-wrap gap-1">
        <Preset label="Tomorrow" onPick={onChange} range={dayAfter(today, 1)} />
        <Preset label="This week" onPick={onChange} range={restOfWeek(today)} />
        <Preset label="Next week" onPick={onChange} range={nextWeek(today)} />
      </div>

      {anchor && (
        <p className="mt-1 text-[10px] text-muted-foreground">
          Click another day to end the range, or the same day again.
        </p>
      )}
    </div>
  );
}

function Preset({
  label,
  range,
  onPick,
}: {
  label: string;
  range: DayRange;
  onPick: (r: DayRange) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onPick(range)}
      className="rounded border border-input px-1.5 py-0.5 text-[10px] hover:bg-muted"
    >
      {label}
    </button>
  );
}

// ------------------------------------------------------------------ date math
// Deliberately local-time throughout: a user picking "the 14th" means the 14th
// where they are, and the backend takes a bare YYYY-MM-DD plus their timezone.

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

export function toIso(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function fromIso(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** ISO dates sort lexicographically, so ordering a pair is a string compare. */
function orderRange(a: string, b: string): DayRange {
  return a <= b ? { start: a, end: b } : { start: b, end: a };
}

/** Monday-first cells for the month, with leading blanks. */
function monthCells(month: Date): (Date | null)[] {
  const first = startOfMonth(month);
  const lead = (first.getDay() + 6) % 7; // Mon = 0
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells: (Date | null)[] = Array.from({ length: lead }, () => null);
  for (let i = 1; i <= days; i++) {
    cells.push(new Date(month.getFullYear(), month.getMonth(), i));
  }
  return cells;
}

function dayAfter(today: Date, n: number): DayRange {
  const d = toIso(addDays(today, n));
  return { start: d, end: d };
}

/** Today through Sunday. Collapses to a single day if today IS Sunday. */
function restOfWeek(today: Date): DayRange {
  const end = addDays(startOfWeek(today), 6);
  return { start: toIso(today), end: toIso(end < today ? today : end) };
}

function nextWeek(today: Date): DayRange {
  const monday = addDays(startOfWeek(today), 7);
  return { start: toIso(monday), end: toIso(addDays(monday, 6)) };
}
